// client/src/context/AuthContext.js
// React Context för autentisering. Mot Railway-backend kör den i demo-mode
// (isAuthBackend() returnerar false); annars riktig JWT-flow mot /api/auth.
import React, { createContext, useContext, useState, useEffect } from 'react';
import API_CONFIG from '../config/apiConfig';

// Create AuthContext
const AuthContext = createContext();

// Custom hook to use AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ✅ Check if backend supports authentication
const isAuthBackend = () => {
  return !API_CONFIG.baseUrl.includes('railway.app');
};

// AuthProvider component
export const AuthProvider = ({ children }) => {
  // State management
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize authentication state on app load
  useEffect(() => {
    initializeAuth();
  }, []);

  // Initialize auth from stored tokens
  const initializeAuth = async () => {
    try {
      // ✅ Skip auth for Railway backend
      if (!isAuthBackend()) {
        setLoading(false);
        setIsAuthenticated(true); // Allow access without auth
        setUser({ email: 'demo@healthdata.se', id: 'demo' }); // Demo user
        return;
      }

      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');

      if (!accessToken || !refreshToken) {
        setLoading(false);
        return;
      }

      // Try to verify current access token
      try {
        const userData = await verifyToken(accessToken);
        setUser(userData);
        setIsAuthenticated(true);
      } catch (tokenError) {
        // Try to refresh the token
        try {
          const newTokens = await refreshTokens(refreshToken);
          localStorage.setItem('accessToken', newTokens.accessToken);
          localStorage.setItem('refreshToken', newTokens.refreshToken);
          
          const userData = await verifyToken(newTokens.accessToken);
          setUser(userData);
          setIsAuthenticated(true);
        } catch (refreshError) {
          clearAuth();
        }
      }
    } catch (error) {
      clearAuth();
    } finally {
      setLoading(false);
    }
  };

  // Verify token with backend
  const verifyToken = async (token) => {
    const response = await fetch(`${API_CONFIG.baseUrl}/auth/verify-token`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Token verification failed');
    }

    const data = await response.json();
    return data.user;
  };

  // Refresh tokens
  const refreshTokens = async (refreshToken) => {
    const response = await fetch(`${API_CONFIG.baseUrl}/auth/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ refreshToken })
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const data = await response.json();
    return data;
  };

  // Register new user
  const register = async (userData) => {
    // ✅ Skip for Railway backend
    if (!isAuthBackend()) {
      return {
        success: true,
        message: 'Demo mode - registration simulated',
        requiresVerification: false
      };
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_CONFIG.baseUrl}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      return {
        success: true,
        message: 'Registrering lyckades! Kontrollera din email för verifiering.',
        requiresVerification: true
      };

    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Login user
  const login = async (credentials) => {
    // ✅ Skip for Railway backend - always succeed
    if (!isAuthBackend()) {
      setUser({ email: credentials.email, id: 'demo' });
      setIsAuthenticated(true);
      return {
        success: true,
        user: { email: credentials.email, id: 'demo' },
        message: 'Demo mode - login successful!'
      };
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_CONFIG.baseUrl}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentials)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store tokens
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);

      // Set user state
      setUser(data.user);
      setIsAuthenticated(true);

      return {
        success: true,
        user: data.user,
        message: 'Inloggning lyckades!'
      };

    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Logout user
const logout = async () => {
  console.log('🚪 User logout (preserving site access)');
  
  try {
    // ❌ SKIP ALL backend calls för att undvika CORS/site auth problem
    // Även för isAuthBackend() - vi vill bara göra local cleanup
    
    console.log('✅ Doing local auth cleanup only');
    
    // ✅ Manual cleanup istället för clearAuth() som kan trigga site auth
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
    
    console.log('✅ User logged out successfully (site access preserved)');
    
    // ❌ INTE anropa clearAuth() som kan ha redirect logic
    
  } catch (error) {
    console.error('Logout error (but continuing):', error);
    
    // Force manual cleanup even on error
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
  }
};

  // Clear authentication state
  const clearAuth = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
  };

  // Get fresh access token (automatic refresh)
  const getValidToken = async () => {
    // ✅ Skip for Railway backend
    if (!isAuthBackend()) {
      return 'demo-token';
    }

    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');

    if (!accessToken || !refreshToken) {
      throw new Error('No authentication tokens');
    }

    try {
      // Try current token first
      await verifyToken(accessToken);
      return accessToken;
    } catch (error) {
      // Token invalid, try refresh
      try {
        const newTokens = await refreshTokens(refreshToken);
        localStorage.setItem('accessToken', newTokens.accessToken);
        localStorage.setItem('refreshToken', newTokens.refreshToken);
        return newTokens.accessToken;
      } catch (refreshError) {
        // Refresh failed, clear auth
        clearAuth();
        throw new Error('Authentication expired');
      }
    }
  };

  // Clear any error state
  const clearError = () => {
    setError(null);
  };

  // Context value
  const value = {
    // State
    user,
    isAuthenticated,
    loading,
    error,

    // Authentication functions
    register,
    login,
    logout,

    // Utility functions
    getValidToken,
    clearError,
    initializeAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};